import React, { useContext, useEffect, useMemo, useState } from "react";
import Footer from "./Footer";
import "../css/shop.scss";
import { Link, useSearchParams } from "react-router-dom";
import { FiHeart, FiMinus, FiPlus, FiSearch, FiSliders, FiX } from "react-icons/fi";
import productData, { categories } from "../Datas/productData";
import Header from "./Header";
import { CartContext } from "./CartContext";
import { formatPrice } from "../config/siteConfig";

export function ProductCard({ product, eager = false }) {
  const { wishlist, toggleWishlist } = useContext(CartContext);
  const saved = wishlist.includes(product.id);

  return (
    <article className="product-card">
      <div className="product-card__media">
        <Link to={`/shopDetails/${product.id}`} onClick={() => window.scrollTo(0, 0)} aria-label={`View ${product.name}`}>
          <img
            src={product.image}
            alt={`${product.name} in ${product.colors[0]}`}
            loading={eager ? "eager" : "lazy"}
            onError={(event) => { event.currentTarget.style.visibility = "hidden"; }}
          />
        </Link>
        {product.badge && <span className="product-card__badge">{product.badge}</span>}
        <button
          type="button"
          className={`product-card__heart ${saved ? "is-saved" : ""}`}
          onClick={() => toggleWishlist(product.id)}
          aria-label={saved ? `Remove ${product.name} from wishlist` : `Save ${product.name} to wishlist`}
          aria-pressed={saved}
        >
          <FiHeart />
        </button>
      </div>
      <Link className="product-card__info" to={`/shopDetails/${product.id}`} onClick={() => window.scrollTo(0, 0)}>
        <h3>{product.name}</h3>
        <span>{formatPrice(product.price)}</span>
        <p>{product.attributes.fit} fit · {product.colors[0]}</p>
      </Link>
    </article>
  );
}

function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { wishlist } = useContext(CartContext);
  const [category, setCategory] = useState(searchParams.get("category") || "All");
  const [gender, setGender] = useState(searchParams.get("gender") || "All");
  const [fit, setFit] = useState(searchParams.get("fit") || "All");
  const [sort, setSort] = useState("featured");
  const [gridSize, setGridSize] = useState(4);
  const [filterOpen, setFilterOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(searchParams.get("search") || "");

  const search = searchParams.get("search") || "";
  const wishlistOnly = searchParams.get("wishlist") === "true";

  useEffect(() => {
    setCategory(searchParams.get("category") || "All");
    setGender(searchParams.get("gender") || "All");
    setFit(searchParams.get("fit") || "All");
    setSearchInput(searchParams.get("search") || "");
  }, [searchParams]);

  useEffect(() => {
    const timer = window.setTimeout(() => setLoading(false), 360);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    document.body.style.overflow = filterOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [filterOpen]);

  const fits = useMemo(
    () => ["All", ...new Set(productData.map((product) => product.attributes.fit))],
    []
  );

  const products = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = productData.filter((product) => {
      const searchable = [product.name, product.category, product.description, ...product.tags].join(" ").toLowerCase();
      return (
        (category === "All" || product.category === category) &&
        (gender === "All" || product.attributes.gender === gender || product.attributes.gender === "Unisex") &&
        (fit === "All" || product.attributes.fit === fit) &&
        (!term || searchable.includes(term)) &&
        (!wishlistOnly || wishlist.includes(product.id))
      );
    });

    return [...filtered].sort((a, b) => {
      if (sort === "price-low") return a.price - b.price;
      if (sort === "price-high") return b.price - a.price;
      if (sort === "newest") return Number(b.newArrival) - Number(a.newArrival);
      return a.id - b.id;
    });
  }, [category, fit, gender, search, sort, wishlist, wishlistOnly]);

  const submitSearch = (event) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (searchInput.trim()) next.set("search", searchInput.trim());
    else next.delete("search");
    setSearchParams(next);
  };

  const clearFilters = () => {
    setCategory("All");
    setGender("All");
    setFit("All");
    setSort("featured");
    setSearchInput("");
    setSearchParams({});
  };

  const setCategoryAndUrl = (value) => {
    setCategory(value);
    const next = new URLSearchParams(searchParams);
    if (value === "All") next.delete("category");
    else next.set("category", value);
    setSearchParams(next);
  };

  return (
    <div className="shop">
      <Header />
      <main className="shop__main">
        <header className="shop__hero">
          <span className="eyebrow">Organic denim and everyday layers</span>
          <h1 className="display-title">
            {wishlistOnly ? "Favorites" : search ? `Results for “${search}”` : category === "All" ? "All products" : category}
            <sup>{products.length}</sup>
          </h1>
          <p>
            Clothes designed for long use, honest wear, and considered care. Every cotton product in this collection begins with organic fiber.
          </p>
        </header>

        <div className="shop__category-strip" aria-label="Product categories">
          {categories.map((item) => (
            <button key={item} className={category === item ? "is-active" : ""} onClick={() => setCategoryAndUrl(item)}>
              {item}
            </button>
          ))}
        </div>

        <div className="shop__toolbar">
          <form className="shop__search" onSubmit={submitSearch} role="search">
            <FiSearch />
            <input value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search the collection" aria-label="Search products" />
          </form>
          <div className="shop__view" aria-label="Grid size">
            <span>View</span>
            {[2, 3, 4].map((columns) => (
              <button key={columns} className={gridSize === columns ? "is-active" : ""} onClick={() => setGridSize(columns)} aria-label={`${columns} products per row`}>
                {Array.from({ length: columns }).map((_, index) => <i key={index} />)}
              </button>
            ))}
          </div>
          <button className="shop__filter-button" onClick={() => setFilterOpen(true)}>
            <FiSliders /> Filter & sort
          </button>
        </div>

        {loading ? (
          <div className="product-grid product-grid--loading" aria-label="Loading products">
            {Array.from({ length: 8 }).map((_, index) => <div key={index}><span className="skeleton" /><i className="skeleton" /><i className="skeleton" /></div>)}
          </div>
        ) : products.length ? (
          <div className="product-grid" style={{ "--grid-columns": gridSize }}>
            {products.map((product, index) => <ProductCard key={product.id} product={product} eager={index < 4} />)}
          </div>
        ) : (
          <div className="shop__empty">
            <h2>{wishlistOnly ? "Your favorites list is empty" : "No products found"}</h2>
            <p>{wishlistOnly ? "Tap the heart on any product to keep it here." : "Try another fit, category, or search term."}</p>
            <button className="app-button" onClick={clearFilters}>View all products</button>
          </div>
        )}

        <section className="shop__care-note">
          <span className="eyebrow">Care less, wear more</span>
          <h2>Wash only when you need to.</h2>
          <p>Air your denim between wears, spot clean when the label allows, and address damage early. It reduces unnecessary washing and helps garments last.</p>
          <Link to="/product-care">Read the wear and care guide</Link>
        </section>
      </main>

      <div className={`filter-backdrop ${filterOpen ? "is-open" : ""}`} onClick={() => setFilterOpen(false)} aria-hidden="true" />
      <aside className={`filter-panel ${filterOpen ? "is-open" : ""}`} aria-hidden={!filterOpen}>
        <div className="filter-panel__header">
          <h2>Filter & sort <sup>{products.length}</sup></h2>
          <button onClick={() => setFilterOpen(false)} aria-label="Close filters"><FiX /></button>
        </div>
        <div className="filter-panel__group">
          <h3><span>Category</span><FiMinus /></h3>
          {categories.map((item) => (
            <label key={item}>
              <input type="radio" name="category" checked={category === item} onChange={() => setCategory(item)} />
              <span>{item}</span>
            </label>
          ))}
        </div>
        <div className="filter-panel__group">
          <h3><span>Collection</span><FiMinus /></h3>
          {["All", "Men", "Women", "Unisex"].map((item) => (
            <label key={item}>
              <input type="radio" name="gender" checked={gender === item} onChange={() => setGender(item)} />
              <span>{item}</span>
            </label>
          ))}
        </div>
        <div className="filter-panel__group">
          <h3><span>Fit</span><FiMinus /></h3>
          {fits.map((item) => (
            <label key={item}>
              <input type="radio" name="fit" checked={fit === item} onChange={() => setFit(item)} />
              <span>{item}</span>
            </label>
          ))}
        </div>
        <div className="filter-panel__group">
          <h3><span>Sort by</span><FiPlus /></h3>
          <select value={sort} onChange={(event) => setSort(event.target.value)}>
            <option value="featured">Featured</option>
            <option value="newest">Newest first</option>
            <option value="price-low">Price: low to high</option>
            <option value="price-high">Price: high to low</option>
          </select>
        </div>
        <div className="filter-panel__actions">
          <button className="app-button app-button--outline" onClick={clearFilters}>Clear all</button>
          <button className="app-button" onClick={() => setFilterOpen(false)}>Show {products.length} products</button>
        </div>
      </aside>

      <Footer />
    </div>
  );
}

export default Shop;
